"use client";

import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useEnvContext } from "next-runtime-env";
import { usePathname } from "next/navigation";

import { getCatalogProductsQueryOptions } from "../api/catalog.query";
import { useCatalogBlockContext } from "../components/CatalogPage/CatalogBlockContext";
import { getCatalogHeaders } from "../server/catalogHeaders.helper";
import type { ActiveCatalogFilters } from "../types/filters.type";
import { buildSearchPathForBff } from "../types/searchTerm.helper";
import { useCatalogSettings } from "./useCatalogSettings.hook";
import { useFilterParams } from "./useFilterParams.hook";

type Props = {
  enabled: boolean;
};

export function usePaginatedCatalogResults({ enabled }: Props) {
  const { NEXT_PUBLIC_BFF_API } = useEnvContext();
  const pathname = usePathname();
  const locale = useLocale();
  const catalogBlockContext = useCatalogBlockContext();
  const [settings] = useCatalogSettings();
  const [filterParams] = useFilterParams();

  const activeFilters = Object.fromEntries(
    Object.entries(filterParams).filter(([, v]) => v !== null),
  ) as ActiveCatalogFilters;

  const { queryKey, queryFn, retry, staleTime } =
    getCatalogProductsQueryOptions({
      apiOrigin: NEXT_PUBLIC_BFF_API!,
      path: buildSearchPathForBff(catalogBlockContext.path || pathname),
      isCMSPath: catalogBlockContext.isCMS,
      pagination: { page: settings.page },
      sort: settings.sort,
      extraHeaders: getCatalogHeaders(),
      filters:
        Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
      locale,
    });

  const query = useQuery({
    queryKey,
    queryFn,
    retry,
    staleTime,
    placeholderData: (prev) => prev,
    enabled,
  });

  const totalProducts = query.data?.pagination?.total ?? 0;
  const isLoading = query.isLoading || query.isPlaceholderData;
  const totalPages = query.data?.pagination?.total_pages ?? 0;
  const isLastPage =
    !isLoading && totalPages > 0 && settings.page === totalPages;

  return {
    query,
    products: query.data?.products ?? [],
    totalProducts,
    isLoading,
    isLastPage,
    totalPages,
    loadMoreProducts: undefined,
    isFetchingMore: false,
  };
}
